export function HighlightedText({
  text,
  searchTerm,
  className,
}: {
  text: string;
  searchTerm: string;
  className?: string;
}) {
  if (!searchTerm) return <p className="text-sm text-gray-500">{text}</p>;

  // Create array of patterns to match (all substrings of search term that are at least 3 characters)
  const createHighlightPatterns = (searchTerm: string): string[] => {
    const words = searchTerm.toLowerCase().split(/\s+/);
    const patterns: string[] = [];

    // Add each word if it's 3+ characters
    words.forEach((word) => {
      if (word.length >= 3) {
        patterns.push(word);
      }
    });

    // Add combinations of consecutive words
    for (let i = 0; i < words.length - 1; i++) {
      const phrase = words.slice(i, i + 2).join(" ");
      if (phrase.length >= 3) {
        patterns.push(phrase);
      }
    }

    // Add the full search term if it's 3+ characters
    if (searchTerm.length >= 3) {
      patterns.push(searchTerm.toLowerCase());
    }

    return patterns;
  };

  const patterns = createHighlightPatterns(searchTerm);
  const textLower = text.toLowerCase();

  // Create a boolean array to track which characters should be highlighted
  const highlightMap = new Array(text.length).fill(false);

  // Mark all matching positions as highlighted
  patterns.forEach((pattern) => {
    let startIndex = 0;
    while (true) {
      const index = textLower.indexOf(pattern, startIndex);
      if (index === -1) break;

      // Mark all characters in this match as highlighted
      for (let i = index; i < index + pattern.length; i++) {
        highlightMap[i] = true;
      }

      startIndex = index + 1;
    }
  });

  // Build the highlighted text by grouping consecutive highlighted/non-highlighted characters
  const parts: Array<{ text: string; highlighted: boolean }> = [];
  let currentHighlighted = highlightMap[0];
  let currentText = text[0] || "";

  for (let i = 1; i < text.length; i++) {
    if (highlightMap[i] === currentHighlighted) {
      currentText += text[i];
    } else {
      // Add the current part
      parts.push({
        text: currentText,
        highlighted: currentHighlighted,
      });

      // Start a new part
      currentHighlighted = highlightMap[i];
      currentText = text[i];
    }
  }

  // Add the last part
  if (currentText) {
    parts.push({
      text: currentText,
      highlighted: currentHighlighted,
    });
  }

  return (
    <p className={className}>
      {parts.map((part, index) =>
        part.highlighted ? (
          <span
            key={index}
            style={{ backgroundColor: "yellow", fontWeight: "bold" }}
          >
            {part.text}
          </span>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </p>
  );
}
